import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useWorkspaceBlobObjectUrl } from '@affine/core/modules/workspace/views/use-workspace-blob-object-url';
import { useI18n } from '@affine/i18n';
import type { BlobReference } from '@streamlain/field-types';
import {
  type MarketplaceVisibility,
  publishRequiresSlotBaseline as sharedRequiresSlotBaseline,
  resolveEnforcedLocale as sharedResolveEnforcedLocale,
} from '@streamlain/marketplace-locale-policy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { uploadWorkspaceBlob } from '../../../modules/streamlain/workflow-viewer/widgets/upload-blob';
import { I18nFieldEditor } from './i18n-field-editor';
import { type LocaleSettings, pickTitle } from './locale-utils';
import * as styles from './styles.css';

/**
 * sl-k94y.7.2: factory for an `I18nFieldEditor.onTranslate` callback that
 * hits the workspace-scoped `/marketplace/translate-descriptions` endpoint
 * for a single field. The endpoint accepts a multi-field batch but the
 * editor's contract is per-field, so the helper sends only the one
 * relevant key and flattens the response back to `{ [locale]: text }`.
 */
export function createWizardFieldTranslate(
  workspaceId: string,
  fieldKey: 'title' | 'description' | 'detailDescription'
): (
  sourceLocale: string,
  sourceText: string
) => Promise<Record<string, string>> {
  return async (sourceLocale, sourceText) => {
    const res = await workspaceApiFetch(
      `/api/workspace/${workspaceId}/marketplace/translate-descriptions`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceLocale,
          sourceTexts: { [fieldKey]: sourceText },
        }),
      },
      marketplaceForegroundPolicy(workspaceId, `translate:${fieldKey}`)
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      throw new Error(body.message ?? `Translate failed (${res.status})`);
    }
    const data = (await res.json()) as {
      translations?: Record<string, Record<string, string>>;
    };
    const out: Record<string, string> = {};
    for (const [locale, payload] of Object.entries(data.translations ?? {})) {
      const text = payload[fieldKey];
      if (typeof text === 'string' && text.length > 0) out[locale] = text;
    }
    return out;
  };
}

export interface PublishMetadataLike {
  // sl-k94y.1.1: BCP-47 keyed title map (visibility-aware baseline enforced
  // server-side: `en` for GLOBAL/SYSTEM, `defaultLocale` for WORKSPACE/TENANT).
  titlesI18n: Record<string, string>;
  description: string;
  category: string;
}

export type PublishVisibilityLike = 'workspace' | 'global';
export type PublishEntityType =
  | 'doc'
  | 'app'
  | 'app_block'
  | 'step'
  | 'workflow'
  | 'automation';
export const MIN_PUBLISH_DESCRIPTION_LENGTH = 50;

function marketplaceBackgroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'workspace-api' as const,
    key: `marketplace:${key}`,
    mode: 'background' as const,
    priority: 'background' as const,
  };
}

function marketplaceForegroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'foreground-action' as const,
    key: `marketplace:${key}`,
    mode: 'foreground' as const,
    priority: 'foreground' as const,
  };
}

/**
 * sl-k94y Phase 4b R3: thin adapter that converts the wizard's lowercase
 * `'workspace' | 'global'` form to the canonical `MarketplaceVisibility`
 * union and delegates to `@streamlain/marketplace-locale-policy` (the
 * single source of truth shared with the backend validator and the admin
 * editor). Keeps the wizard's local visibility shape intact while removing
 * the previous policy duplicate.
 */
function toMarketplaceVisibility(
  visibility: PublishVisibilityLike
): MarketplaceVisibility {
  return visibility === 'global' ? 'GLOBAL' : 'WORKSPACE';
}

export function resolvePublishEnforcedLocale(
  visibility: PublishVisibilityLike,
  defaultLocale: string
): string {
  return sharedResolveEnforcedLocale(
    toMarketplaceVisibility(visibility),
    defaultLocale
  );
}

/**
 * Returns the locale-keyed entry the preview prompt and other display
 * sites should seed from. The seeded locale follows the policy so the
 * downstream UI matches what the backend would persist.
 */
export function getPrimaryPublishDescription(
  values: Record<string, string>,
  enforcedLocale: string
): string {
  return values[enforcedLocale]?.trim() ?? '';
}

/**
 * sl-k94y.7.2: visibility-aware variant of the previous `en`-hard-coded
 * gate. Wizard validation must check whichever locale the backend will
 * enforce for the *current* visibility (Rule 7 — align at the source);
 * the 50-char quality bar is a wizard-only UX guard that applies to the
 * same locale.
 */
export function isPublishDescriptionComplete(
  values: Record<string, string>,
  enforcedLocale: string
): boolean {
  const text = values[enforcedLocale]?.trim() ?? '';
  return text.length >= MIN_PUBLISH_DESCRIPTION_LENGTH;
}

/**
 * sl-k94y Phase 4b R3 (Cluster B): the slot-baseline rule now applies to
 * every visibility — backend's `validatePublishedSlots` no longer
 * early-returns for deployment-bound publishes. This function delegates
 * to the shared package so flipping the policy in one place flips it
 * everywhere.
 */
export function publishRequiresSlotBaseline(
  _visibility: PublishVisibilityLike
): boolean {
  return sharedRequiresSlotBaseline();
}

/**
 * sl-k94y.7.2: maps a backend `MarketplaceLocaleRequired.fieldPath` to the
 * matching DOM anchor id rendered by the wizard. Backend shapes:
 *   - metadata:   `['titlesI18n']` / `['descriptionsI18n']`
 *   - step kind:  `[<slot-key>, <slot-id>, 'descriptionsI18n', <locale>]`
 *   - workflow:   `['steps', <step-id>, <slot-key>, <slot-id>, ...]`
 * Returns `null` if no matching anchor was rendered (caller should fall
 * back to surfacing the error message without auto-scroll).
 */
export function resolvePublishFieldAnchor(
  fieldPath: ReadonlyArray<string>
): string | null {
  for (let i = 0; i < fieldPath.length; i++) {
    const seg = fieldPath[i];
    if (i === 0 && seg === 'titlesI18n') return 'publish-field-titlesI18n';
    if (i === 0 && seg === 'descriptionsI18n') {
      return 'publish-field-descriptionsI18n';
    }
    if (
      seg === 'inputSlots' ||
      seg === 'configSlots' ||
      seg === 'outputSlots'
    ) {
      const slotId = fieldPath[i + 1];
      if (typeof slotId === 'string' && slotId.length > 0) {
        return `publish-slot-${slotId}`;
      }
    }
  }
  return null;
}

export function buildDefaultPreviewPrompt(
  metadata: PublishMetadataLike,
  language: string,
  settings: LocaleSettings
): string {
  const title = pickTitle(metadata.titlesI18n, language, settings);
  const parts = [
    `A professional cover image for a workflow automation template`,
    title ? `called "${title}"` : '',
    metadata.category ? `in the ${metadata.category} category` : '',
    metadata.description ? `that ${metadata.description.slice(0, 100)}` : '',
  ].filter(Boolean);
  return `${parts.join(' ')}. Clean, modern, minimal illustration style with subtle gradients. No text.`;
}

export function PublishPreviewImageEditor({
  workspaceId,
  blobId,
  metadata,
  nodeDescriptions,
  onUploaded,
}: {
  workspaceId?: string;
  blobId?: string;
  metadata: PublishMetadataLike;
  nodeDescriptions?: string[];
  onUploaded: (blobId: string) => void;
}) {
  const t = useI18n();
  const picker = useLocalePicker();
  const fileRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'idle' | 'ai' | 'upload'>('idle');
  const [prompt, setPrompt] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlobId, setPreviewBlobId] = useState<string | null>(
    blobId ?? null
  );
  const workspacePreviewUrl = useWorkspaceBlobObjectUrl(
    workspaceId,
    previewBlobId
  );
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promptSuggested, setPromptSuggested] = useState(false);

  useEffect(() => {
    if (
      mode !== 'ai' ||
      promptSuggested ||
      !workspaceId ||
      !nodeDescriptions?.length ||
      !picker
    ) {
      return;
    }
    setPromptSuggested(true);
    workspaceApiFetch(
      `/api/workspace/${workspaceId}/marketplace/suggest-prompt`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodeDescriptions,
          title: picker.pickTitle(metadata.titlesI18n),
          category: metadata.category,
        }),
      },
      marketplaceBackgroundPolicy(workspaceId, 'suggest-preview-prompt')
    )
      .then(r => r.json())
      .then((data: { prompt: string }) => {
        if (data.prompt) setPrompt(data.prompt);
      })
      .catch(() => {});
  }, [
    mode,
    promptSuggested,
    workspaceId,
    nodeDescriptions,
    metadata.titlesI18n,
    metadata.category,
    picker,
  ]);

  useEffect(() => {
    if (!promptSuggested && !generating && picker) {
      setPrompt(
        buildDefaultPreviewPrompt(metadata, picker.language, picker.settings)
      );
    }
  }, [
    metadata.titlesI18n,
    metadata.category,
    metadata.description,
    generating,
    promptSuggested,
    metadata,
    picker,
  ]);

  useEffect(() => {
    setPreviewBlobId(blobId ?? null);
  }, [blobId]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const displayUrl = useMemo(() => {
    if (previewUrl) return previewUrl;
    return workspacePreviewUrl;
  }, [previewUrl, workspacePreviewUrl]);

  const handleGenerate = useCallback(async () => {
    if (!workspaceId || !prompt.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/marketplace/generate-preview`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: prompt.trim() }),
        },
        marketplaceForegroundPolicy(workspaceId, 'generate-preview')
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(
          err.message ??
            t['com.streamlain.marketplace.publish.preview.error.generation']()
        );
      }
      const data = (await res.json()) as {
        blobId: string;
        revisedPrompt?: string;
      };
      setPreviewUrl(null);
      setPreviewBlobId(data.blobId);
      onUploaded(data.blobId);
      if (data.revisedPrompt) setPrompt(data.revisedPrompt);
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t['com.streamlain.marketplace.publish.preview.error.generation']()
      );
    } finally {
      setGenerating(false);
    }
  }, [workspaceId, prompt, onUploaded, t]);

  const handleFile = useCallback(
    async (file: File) => {
      if (!workspaceId) return;
      if (!file.type.startsWith('image/')) {
        setError(
          t['com.streamlain.marketplace.publish.preview.error.image-only']()
        );
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError(
          t['com.streamlain.marketplace.publish.preview.error.max-size']()
        );
        return;
      }
      setError(null);
      setPreviewUrl(URL.createObjectURL(file));
      setUploading(true);
      try {
        const blob: BlobReference = await uploadWorkspaceBlob(
          workspaceId,
          file
        );
        setPreviewBlobId(blob.blobId);
        onUploaded(blob.blobId);
      } catch (uploadError) {
        setError(
          uploadError instanceof Error
            ? uploadError.message
            : t['com.streamlain.marketplace.publish.preview.error.upload']()
        );
      } finally {
        setUploading(false);
      }
    },
    [workspaceId, onUploaded, t]
  );

  if (mode === 'idle' && !displayUrl) {
    return (
      <div>
        <div className={styles.detailSectionTitle}>
          {t['com.streamlain.marketplace.publish.preview.optional']()}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={() => setMode('ai')}
            style={{ flex: 1 }}
          >
            {t['com.streamlain.marketplace.publish.preview.generate-ai']()}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => {
              setMode('upload');
              fileRef.current?.click();
            }}
            style={{ flex: 1 }}
          >
            {t['com.streamlain.marketplace.publish.preview.upload']()}
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            setMode('idle');
          }}
        />
      </div>
    );
  }

  if (mode === 'ai' || (mode === 'idle' && displayUrl && !blobId)) {
    return (
      <div>
        <div className={styles.detailSectionTitle}>
          {t['com.streamlain.marketplace.publish.preview.ai-generated']()}
        </div>
        {displayUrl && (
          <div style={{ marginBottom: '8px', textAlign: 'center' }}>
            <img
              src={displayUrl}
              alt={t[
                'com.streamlain.marketplace.publish.preview.generated-alt'
              ]()}
              style={{
                maxWidth: '100%',
                maxHeight: '160px',
                borderRadius: '6px',
                objectFit: 'cover',
              }}
            />
          </div>
        )}
        <textarea
          className={styles.reportDetails}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder={t[
            'com.streamlain.marketplace.publish.preview.prompt-placeholder'
          ]()}
          style={{ minHeight: '60px', marginBottom: '8px' }}
        />
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={() => void handleGenerate()}
            disabled={generating || !prompt.trim()}
            style={{ flex: 1 }}
          >
            {generating
              ? t['com.streamlain.marketplace.publish.preview.generating']()
              : displayUrl
                ? t['com.streamlain.marketplace.publish.preview.regenerate']()
                : t['com.streamlain.marketplace.publish.preview.generate']()}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => setMode('idle')}
          >
            {displayUrl
              ? t['com.streamlain.marketplace.publish.preview.done']()
              : t['com.streamlain.marketplace.publish.preview.cancel']()}
          </button>
        </div>
        {error && (
          <div
            style={{
              color: 'var(--streamlain-error-color, #D32F2F)',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className={styles.detailSectionTitle}>
        {t['com.streamlain.marketplace.publish.preview.title']()}
      </div>
      {displayUrl && (
        <div style={{ marginBottom: '8px', textAlign: 'center' }}>
          <img
            src={displayUrl}
            alt={t['com.streamlain.marketplace.publish.preview.alt']()}
            style={{
              maxWidth: '100%',
              maxHeight: '160px',
              borderRadius: '6px',
              objectFit: 'cover',
            }}
          />
        </div>
      )}
      {(uploading || generating) && (
        <div className={styles.spinner} style={{ margin: '8px auto' }} />
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={() => setMode('ai')}
          style={{ flex: 1 }}
        >
          {t['com.streamlain.marketplace.publish.preview.regenerate-ai']()}
        </button>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={() => fileRef.current?.click()}
          style={{ flex: 1 }}
        >
          {t['com.streamlain.marketplace.publish.preview.replace-upload']()}
        </button>
      </div>
      {error && (
        <div
          style={{
            color: 'var(--streamlain-error-color, #D32F2F)',
            fontSize: '12px',
            marginTop: '4px',
          }}
        >
          {error}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
    </div>
  );
}

export function PublishVisibilitySelector({
  value,
  onChange,
}: {
  value: PublishVisibilityLike;
  onChange: (value: PublishVisibilityLike) => void;
}) {
  const t = useI18n();
  return (
    <div style={{ paddingTop: '4px' }}>
      <div
        className={styles.detailSectionTitle}
        style={{ marginBottom: '16px' }}
      >
        {t['com.streamlain.marketplace.publish.visibility.title']()}
      </div>
      <p style={{ fontSize: '13px', color: '#999', marginBottom: '20px' }}>
        {t['com.streamlain.marketplace.publish.visibility.subtitle']()}
      </p>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '12px 16px',
          borderRadius: '8px',
          cursor: 'pointer',
          backgroundColor:
            value === 'workspace' ? 'rgba(74, 144, 217, 0.1)' : 'transparent',
          border:
            value === 'workspace' ? '1px solid #4A90D9' : '1px solid #333',
          marginBottom: '12px',
        }}
      >
        <input
          type="radio"
          name="visibility"
          checked={value === 'workspace'}
          onChange={() => onChange('workspace')}
          style={{ marginTop: '2px' }}
        />
        <div>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#eee' }}>
            {t[
              'com.streamlain.marketplace.publish.visibility.workspace.title'
            ]()}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
            {t[
              'com.streamlain.marketplace.publish.visibility.workspace.description'
            ]()}
          </div>
        </div>
      </label>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '12px 16px',
          borderRadius: '8px',
          cursor: 'pointer',
          backgroundColor:
            value === 'global' ? 'rgba(74, 144, 217, 0.1)' : 'transparent',
          border: value === 'global' ? '1px solid #4A90D9' : '1px solid #333',
        }}
      >
        <input
          type="radio"
          name="visibility"
          checked={value === 'global'}
          onChange={() => onChange('global')}
          style={{ marginTop: '2px' }}
        />
        <div>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#eee' }}>
            {t['com.streamlain.marketplace.publish.visibility.global.title']()}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
            {t[
              'com.streamlain.marketplace.publish.visibility.global.description'
            ]()}
          </div>
        </div>
      </label>
    </div>
  );
}

/**
 * sl-eglf.5.2 + Phase 4b rework: Source-side reference for the
 * suggest-descriptions endpoint. The wizard MUST NOT pre-load templateData
 * (DIP — single source of truth lives on the backend); it sends a
 * discriminated reference and the backend's `resolveEntityRef` switches on
 * `source`. Mirrors backend `PublishEntityRef`
 * (services/marketplace/marketplace-publish-ai.service.ts).
 */
export type DescriptionEditorEntityRef =
  | {
      source: 'doc';
      docId: string;
    }
  | {
      source: 'app';
      rootDocId: string;
    }
  | {
      source: 'app_block';
      rootDocId: string;
      customAppId: string;
      appBlockId: string;
    }
  | {
      source: 'automation';
      automationBlockId: string;
    }
  | {
      source: 'workflow';
      workflowId: string;
    }
  | {
      source: 'step';
      workflowId: string;
      stepId: string;
    };

/**
 * sl-k94y.7.2: PublishDescriptionEditor — refactored to delegate the
 * locale-keyed text editing to `I18nFieldEditor` (the reusable per-locale
 * field editor) and keep two top-level concerns colocated:
 *
 *   1. AI-Suggest button — generates `{ [locale]: text }` for ALL deployment
 *      locales in one shot from templateData. Stays a separate button
 *      because suggest != translate (different prompt, different cost).
 *   2. Char-counter — the wizard's 50-char quality bar applied to the
 *      visibility-aware enforced locale (GLOBAL → 'en', WORKSPACE →
 *      defaultLocale). Mirrors `MarketplaceValidationService.resolveLocalePolicy`
 *      so submit cannot 422 on a "I filled defaultLocale but the caller
 *      validated en" mismatch. Counter renders below the editor so it's
 *      always visible regardless of the active locale tab.
 *
 * The previous "add language from SUPPORTED_LANGUAGES" dropdown is gone:
 * available locales come from `LocaleSettingsService`, not from a
 * static client-side list. Adding a locale outside the deployment's
 * `availableLocales` would publish content that nobody renders anyway.
 */
export function PublishDescriptionEditor({
  workspaceId,
  entityType,
  entityRef,
  title,
  category,
  summary,
  fieldHints,
  values,
  onChange,
  enforcedLocale,
}: {
  workspaceId?: string;
  entityType: PublishEntityType;
  entityRef?: DescriptionEditorEntityRef;
  title: string;
  category: string;
  summary: string;
  fieldHints?: string[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /**
   * sl-k94y.7.2: Locale the backend will enforce for this publish (caller
   * resolves via `resolvePublishEnforcedLocale(visibility, defaultLocale)`).
   * Drives required-marker, the 50-char counter target, and
   * `getPrimaryPublishDescription` so the editor always points at the
   * locale that gates submission.
   */
  enforcedLocale: string;
}) {
  const t = useI18n();
  const picker = useLocalePicker();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onTranslate = useMemo(
    () =>
      workspaceId
        ? createWizardFieldTranslate(workspaceId, 'description')
        : undefined,
    [workspaceId]
  );

  const handleGenerate = useCallback(async () => {
    if (!workspaceId || !picker || !entityRef) return;
    setBusy(true);
    setError(null);
    try {
      const response = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/marketplace/suggest-descriptions`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entityType,
            title,
            category,
            summary,
            fieldHints,
            // sl-k94y.7.2: ask for every available locale, not the previously
            // typed-in subset. Backend extracts templateData itself, so it can
            // generate from scratch even if `values` is empty.
            languages: picker.settings.availableLocales,
            entityRef,
          }),
        },
        marketplaceForegroundPolicy(workspaceId, 'suggest-descriptions')
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(
          body.message ??
            t['com.streamlain.marketplace.publish.description.error.failed']()
        );
      }
      const data = (await response.json()) as {
        descriptionsI18n?: Record<string, string>;
      };
      if (!data.descriptionsI18n) {
        throw new Error(
          t['com.streamlain.marketplace.publish.description.error.no-payload']()
        );
      }
      onChange({ ...values, ...data.descriptionsI18n });
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t['com.streamlain.marketplace.publish.description.error.failed']()
      );
    } finally {
      setBusy(false);
    }
  }, [
    workspaceId,
    picker,
    entityType,
    entityRef,
    title,
    category,
    summary,
    fieldHints,
    onChange,
    values,
    t,
  ]);

  if (!picker) {
    return (
      <div style={{ minHeight: 120 }} aria-busy="true">
        {t['com.streamlain.marketplace.publish.description.title']()}…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
        }}
      >
        <button
          type="button"
          className={styles.cancelButton}
          disabled={busy || !workspaceId || !title.trim() || !entityRef}
          onClick={() => void handleGenerate()}
        >
          {busy
            ? t['com.streamlain.marketplace.publish.description.generating']()
            : t['com.streamlain.marketplace.publish.description.generate']()}
        </button>
      </div>

      <I18nFieldEditor
        value={values}
        onChange={onChange}
        locales={picker.settings.availableLocales}
        defaultLocale={picker.settings.defaultLocale}
        label={t['com.streamlain.marketplace.publish.description.title']()}
        placeholder={t[
          'com.streamlain.marketplace.publish.description.placeholder'
        ]({
          language: picker.settings.defaultLocale.toUpperCase(),
        })}
        multiline
        requiredLocales={[enforcedLocale]}
        onTranslate={onTranslate}
        onTranslateError={err =>
          setError(
            err instanceof Error
              ? err.message
              : t[
                  'com.streamlain.marketplace.publish.description.error.failed'
                ]()
          )
        }
      />

      <div style={{ fontSize: '12px', color: '#888' }}>
        {t['com.streamlain.marketplace.publish.description.min-length']({
          count: String(MIN_PUBLISH_DESCRIPTION_LENGTH),
        })}
        {' — '}
        <span className={styles.ratingCharCount}>
          {getPrimaryPublishDescription(values, enforcedLocale).length} /{' '}
          {MIN_PUBLISH_DESCRIPTION_LENGTH} min
        </span>
      </div>

      {error && (
        <div
          style={{
            color: 'var(--streamlain-error-color, #D32F2F)',
            fontSize: '12px',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export function PublishConsentSection({
  title,
  category,
  extraSummary,
  consentGdpr,
  consentLicense,
  onConsentGdprChange,
  onConsentLicenseChange,
}: {
  title: string;
  category: string;
  extraSummary?: string;
  consentGdpr: boolean;
  consentLicense: boolean;
  onConsentGdprChange: (value: boolean) => void;
  onConsentLicenseChange: (value: boolean) => void;
}) {
  const t = useI18n();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <div className={styles.detailSectionTitle}>
        {t['com.streamlain.marketplace.publish.consent.title']()}
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={consentGdpr}
          onChange={e => onConsentGdprChange(e.target.checked)}
          style={{ marginTop: '2px' }}
        />
        <span>
          {t['com.streamlain.marketplace.publish.consent.identity']()}
        </span>
      </label>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={consentLicense}
          onChange={e => onConsentLicenseChange(e.target.checked)}
          style={{ marginTop: '2px' }}
        />
        <span>{t['com.streamlain.marketplace.publish.consent.license']()}</span>
      </label>

      <div className={styles.installSummary} style={{ marginTop: '8px' }}>
        {t['com.streamlain.marketplace.publish.consent.summary']({
          title,
          category,
        })}
        {extraSummary ? ` ${extraSummary}` : ''}
      </div>
    </div>
  );
}
